import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import axios from "../../lib/http/axiosConfig";
import "./Sidebar.css";
import Information from "./Informationtab/Information";
import Mission from "./Missiontab/Mission";
import Data from "./Datatab/Data";
import Energy from "./Datatab/Energy";
import "./Missiontab/Mission.css";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { setActiveTab } from "../../features/TabSlice";
import { setFarmId, replaceFarmList, appendFarmList } from "../../features/FarmSlice";
import { useT } from "../../i18n";

const TOOLTIP_MARGIN = 12;
const TOOLTIP_OFFSET_Y = 8;
const TOOLTIP_DESKTOP_MAX_WIDTH = 340;
const TOOLTIP_MOBILE_MAX_WIDTH = 360;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const SIDEBAR_TAB_STORAGE_PREFIX = "tlatfarm:sidebar";
const SIDEBAR_FARM_STORAGE_KEY = "tlatfarm:sidebar:activeFarmId";
// "Data" 키는 화면에 "작물분석"으로 보인다. 저장된 탭·DiagnosisRuns 의 탭 비교가 키를 쓰므로 그대로 둔다.
const VALID_SIDEBAR_TABS = new Set(["Information", "Mission", "Data", "Energy"]);

const readStoredFarmId = () => {
  if (typeof window === "undefined") return null;

  try {
    const savedFarmId = window.sessionStorage.getItem(SIDEBAR_FARM_STORAGE_KEY);
    return savedFarmId == null || savedFarmId === "" ? null : savedFarmId;
  } catch (error) {
    return null;
  }
};

const saveStoredFarmId = (farmId) => {
  if (farmId == null || farmId === "" || typeof window === "undefined") return;

  try {
    window.sessionStorage.setItem(SIDEBAR_FARM_STORAGE_KEY, String(farmId));
  } catch (error) {
    // Keep farm switching usable even if sessionStorage is unavailable.
  }
};
const getSidebarTabStorageKey = (farmId) => {
  if (farmId == null || farmId === "") return null;
  return `${SIDEBAR_TAB_STORAGE_PREFIX}:${farmId}:activeTab`;
};

const readStoredSidebarTab = (farmId) => {
  const key = getSidebarTabStorageKey(farmId);
  if (!key || typeof window === "undefined") return null;

  try {
    const savedTab = window.sessionStorage.getItem(key);
    return VALID_SIDEBAR_TABS.has(savedTab) ? savedTab : null;
  } catch (error) {
    return null;
  }
};

const saveStoredSidebarTab = (farmId, tab) => {
  const key = getSidebarTabStorageKey(farmId);
  if (!key || typeof window === "undefined") return;

  try {
    if (tab == null || tab === "") {
      window.sessionStorage.removeItem(key);
      return;
    }

    if (VALID_SIDEBAR_TABS.has(tab)) {
      window.sessionStorage.setItem(key, tab);
    }
  } catch (error) {
    // Keep tab switching usable even if sessionStorage is unavailable.
  }
};
export default function Sidebar() {
  const dispatch = useDispatch();
  const t = useT();
  const tab = useSelector((state) => state.tab.activeTab);
  const isCellPreviewOpen = useSelector((state) => state.tab.isCellPreviewOpen);
  const isAnalysis = useSelector((state) => state.tab.toggle);
  const farmId = useSelector((state) => state.farm.farmId);
  const selectedDrone = useSelector((state) => state.drone.selectedDrone);
  // 미션 수행 중(시작~복귀까지)에는 농장/탭 전환을 막는다. 전환 시 소켓이 살아있는
  // 채로 드론 데이터가 계속 흘러 "멈춤 안내와 실제 동작이 어긋나는" 상태가 되므로,
  // 드론이 착륙(disarm)해 missionStatus/missionReturning이 풀릴 때까지 잠근다.
  const missionStatus = useSelector((state) => state.drone.control?.missionStatus);
  const missionReturning = useSelector((state) => state.drone.missionReturning);
  const navLocked = Boolean(missionStatus || missionReturning);

  const notifyNavLocked = useCallback(() => {
    toast.warn(t("common.nav.lockedDuringMission"), {
      toastId: "nav-locked-during-mission",
    });
  }, [t]);

  const [farms, setFarmList] = useState([]);
  const [isMobile, setIsMobile] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const didInitMobileRef = useRef(false);

  const [cursorId, setCursorId] = useState(null);
  const [hasNextPage, setHasNextPage] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  // fetchFarmList의 useCallback이 cursorId/hasNextPage/isLoading/farms.length/farmId 변경마다
  // 새로 생성되면 observer useEffect가 재실행되어 sentinel이 viewport에 있을 때 즉시 콜백
  // 발동 → 무한 루프. ref로 stale closure 회피하고 콜백 ID 고정.
  const fetchStateRef = useRef({
    cursorId: null,
    hasNextPage: true,
    isLoading: false,
    farmsLength: 0,
    farmId: null,
  });
  fetchStateRef.current = {
    cursorId,
    hasNextPage,
    isLoading,
    farmsLength: farms.length,
    farmId,
  };
  const sentinelRef = useRef(null);
  const sidebarRef = useRef(null);
  const activeTooltipButtonRef = useRef(null);
  const [tooltipState, setTooltipState] = useState({
    open: false,
    text: "",
    top: 0,
    left: 0,
    width: TOOLTIP_DESKTOP_MAX_WIDTH,
    isMobile: false,
  });

  const closeTooltip = useCallback(() => {
    activeTooltipButtonRef.current = null;
    setTooltipState((prev) => (prev.open ? { ...prev, open: false } : prev));
  }, []);

  const openTooltip = useCallback((buttonEl) => {
    if (!buttonEl || typeof window === "undefined") return;
    const text = buttonEl.getAttribute("data-tip");
    if (!text) return;

    const rect = buttonEl.getBoundingClientRect();
    const isMobileViewport = window.innerWidth <= 900;
    const width = Math.min(
      isMobileViewport ? TOOLTIP_MOBILE_MAX_WIDTH : TOOLTIP_DESKTOP_MAX_WIDTH,
      window.innerWidth - TOOLTIP_MARGIN * 2
    );
    const left = isMobileViewport
      ? window.innerWidth / 2
      : clamp(
          rect.left + rect.width / 2,
          TOOLTIP_MARGIN + width / 2,
          window.innerWidth - TOOLTIP_MARGIN - width / 2
        );

    activeTooltipButtonRef.current = buttonEl;
    setTooltipState({
      open: true,
      text,
      top: rect.bottom + TOOLTIP_OFFSET_Y,
      left,
      width,
      isMobile: isMobileViewport,
    });
  }, []);

  const repositionTooltip = useCallback(() => {
    const buttonEl = activeTooltipButtonRef.current;
    if (!buttonEl || !document.body.contains(buttonEl)) {
      closeTooltip();
      return;
    }
    openTooltip(buttonEl);
  }, [closeTooltip, openTooltip]);

  const fetchFarmList = useCallback(async () => {
    const snapshot = fetchStateRef.current;
    if (snapshot.isLoading) return;
    if (!snapshot.hasNextPage && snapshot.farmsLength > 0) return;
    setIsLoading(true);

    try {
      const params = { size: 50 };
      if (snapshot.cursorId != null) {
        params.id = snapshot.cursorId;
      }
      const { data } = await axios.get("/api/farms", { params });
      const payload = data?.data ?? {};
      const farmList = Array.isArray(payload.content) ? payload.content : [];
      const nextCursorId = payload.nextCursorId ?? null;
      const hasNext = Boolean(payload.hasNext);

      if (!farmList.length && (nextCursorId == null || hasNext === false)) {
        if (snapshot.cursorId == null) dispatch(replaceFarmList([]));
        setHasNextPage(false);
        return;
      }

      setFarmList((prev) => [...prev, ...farmList]);
      // FarmOverlay 등 다른 컴포넌트가 같은 목록을 다시 fetch하지 않도록 Redux 에도 반영.
      // 첫 페이지(cursorId == null)는 재마운트 시 중복 누적을 막기 위해 교체, 이후 스크롤 페이지는 이어붙인다.
      dispatch(
        snapshot.cursorId == null ? replaceFarmList(farmList) : appendFarmList(farmList)
      );

      if (snapshot.cursorId == null && snapshot.farmId == null && farmList.length > 0) {
        const savedFarmId = readStoredFarmId();
        const savedFarm = farmList.find((farm) => String(farm.id) === savedFarmId);
        dispatch(setFarmId(savedFarm?.id ?? farmList[0].id));
      }

      setCursorId(nextCursorId);
      setHasNextPage(hasNext);
      // console.log("Farm list: ", farmList);
    } catch (error) {
      console.error("Failed to load farm list: ", error);
      if (fetchStateRef.current.farmsLength > 0) {
        setHasNextPage(false);
      }
    } finally {
      setIsLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchFarmList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!sentinelRef.current) return;
    const el = sentinelRef.current;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          fetchFarmList();
        }
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 1.0,
      }
    );

    observer.observe(el);
    return () => observer.unobserve(el);
  }, [fetchFarmList]);
  useEffect(() => {
    if (farmId == null) {
      return;
    }

    const savedTab = readStoredSidebarTab(farmId);
    if (!savedTab) {
      return;
    }

    if (savedTab === "Mission" && selectedDrone == null) return;

    if (tab !== savedTab) {
      dispatch(setActiveTab(savedTab));
      return;
    }

  }, [farmId, selectedDrone, tab, dispatch]);


  useEffect(() => {
    if (!isMobile && farmId != null && (tab == null || tab === "")) {
      dispatch(setActiveTab("Information"));
    }
  }, [farmId, tab, isMobile, dispatch]);

  useEffect(() => {
    if (selectedDrone == null && tab === "Mission") {
      dispatch(setActiveTab("Information"));
    }
  }, [selectedDrone, tab, dispatch]);

  useEffect(() => {
    if (farmId == null && farms.length > 0) {
      const savedFarmId = readStoredFarmId();
      const savedFarm = farms.find((farm) => String(farm.id) === savedFarmId);
      dispatch(setFarmId(savedFarm?.id ?? farms[0].id));
    }
  }, [farmId, farms, dispatch]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 900px)");
    const handler = (event) => setIsMobile(event.matches);
    handler(mq);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  // 데이터 탭에서 촬영 회차를 고르면 지도에 그 회차가 뜬다. 모바일은 패널이 지도를
  // 덮고 있어 눌러도 뒤에서 무슨 일이 났는지 안 보이므로 패널을 접어 준다.
  // 저장값도 같이 비운다 — 안 그러면 위의 복원 효과가 곧바로 탭을 다시 연다.
  // 지도가 보낸 알림(source: "map")은 지도에서 고른 것이라 접을 이유가 없다.
  const diagnosisFocusAt = useSelector((state) =>
    state.tab.diagnosisFocus?.source === "map"
      ? null
      : state.tab.diagnosisFocus?.at ?? null
  );
  useEffect(() => {
    if (!diagnosisFocusAt || !isMobile) return;
    saveStoredSidebarTab(farmId, null);
    dispatch(setActiveTab(null));
  }, [diagnosisFocusAt, isMobile, farmId, dispatch]);

  useEffect(() => {
    if (isMobile) {
      if (!didInitMobileRef.current) {
        if (!readStoredSidebarTab(farmId)) {
          dispatch(setActiveTab(null));
        }
        didInitMobileRef.current = true;
      }
      return;
    }
    didInitMobileRef.current = false;
  }, [isMobile, farmId, dispatch]);

  useEffect(() => {
    if (!isDropdownOpen) return;
    const handleClick = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [isDropdownOpen]);

  useEffect(() => {
    closeTooltip();
  }, [tab, closeTooltip]);

  // 모바일 하단 시트의 실제 높이를 CSS 변수로 알린다. 지도 위 버튼(전체/진단/±)과
  // 셀 선택 바가 탭을 열고 닫아도 항상 시트 바로 위에 붙게 CalmTheme.css(≤900px)가 읽는다.
  useEffect(() => {
    const el = sidebarRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const root = document.documentElement;
    const apply = () => root.style.setProperty("--calm-sheet-h", `${el.offsetHeight}px`);
    apply();
    const observer = new ResizeObserver(apply);
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.removeProperty("--calm-sheet-h");
    };
  }, []);

  useEffect(() => {
    const rootEl = sidebarRef.current;
    if (!rootEl) return;

    const isMobileViewport = () =>
      typeof window !== "undefined" && window.innerWidth <= 900;

    const findTooltipButton = (target) => {
      if (!(target instanceof Element)) return null;
      const buttonEl = target.closest(".sb-help[data-tip]");
      if (!buttonEl || !rootEl.contains(buttonEl)) return null;
      return buttonEl;
    };

    const handlePointerOver = (event) => {
      if (isMobileViewport()) return;
      const buttonEl = findTooltipButton(event.target);
      if (!buttonEl) return;
      openTooltip(buttonEl);
    };

    const handlePointerOut = (event) => {
      if (isMobileViewport()) return;
      const buttonEl = findTooltipButton(event.target);
      if (!buttonEl) return;

      const nextTarget = event.relatedTarget;
      if (nextTarget instanceof Element && buttonEl.contains(nextTarget)) return;
      if (activeTooltipButtonRef.current === buttonEl) {
        closeTooltip();
      }
    };

    const handleFocusIn = (event) => {
      if (isMobileViewport()) return;
      const buttonEl = findTooltipButton(event.target);
      if (!buttonEl) return;
      openTooltip(buttonEl);
    };

    const handleFocusOut = (event) => {
      if (isMobileViewport()) return;
      const buttonEl = findTooltipButton(event.target);
      if (!buttonEl) return;

      const nextTarget = event.relatedTarget;
      if (nextTarget instanceof Element && buttonEl.contains(nextTarget)) return;
      if (activeTooltipButtonRef.current === buttonEl) {
        closeTooltip();
      }
    };

    const handleClick = (event) => {
      if (!isMobileViewport()) return;
      const buttonEl = findTooltipButton(event.target);
      if (!buttonEl) return;

      event.preventDefault();
      event.stopPropagation();

      if (activeTooltipButtonRef.current === buttonEl) {
        closeTooltip();
        return;
      }
      openTooltip(buttonEl);
    };

    rootEl.addEventListener("pointerover", handlePointerOver);
    rootEl.addEventListener("pointerout", handlePointerOut);
    rootEl.addEventListener("focusin", handleFocusIn);
    rootEl.addEventListener("focusout", handleFocusOut);
    rootEl.addEventListener("click", handleClick);

    return () => {
      rootEl.removeEventListener("pointerover", handlePointerOver);
      rootEl.removeEventListener("pointerout", handlePointerOut);
      rootEl.removeEventListener("focusin", handleFocusIn);
      rootEl.removeEventListener("focusout", handleFocusOut);
      rootEl.removeEventListener("click", handleClick);
    };
  }, [openTooltip, closeTooltip]);

  useEffect(() => {
    if (!tooltipState.open) return;

    const handlePointerDown = (event) => {
      const activeButton = activeTooltipButtonRef.current;
      if (activeButton && activeButton.contains(event.target)) return;
      closeTooltip();
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") closeTooltip();
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("resize", repositionTooltip);
    window.addEventListener("scroll", repositionTooltip, true);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("resize", repositionTooltip);
      window.removeEventListener("scroll", repositionTooltip, true);
    };
  }, [tooltipState.open, closeTooltip, repositionTooltip]);

  return (
    <aside ref={sidebarRef} className="tf-sidebar sb-shell">
      {isMobile && farms.length > 0 && !isCellPreviewOpen && !isAnalysis && (
        <div className="mobile-farm-dropdown" ref={dropdownRef}>
          <button
            type="button"
            className="mobile-farm-toggle"
            onClick={() => setIsDropdownOpen((prev) => !prev)}
          >
            <span className="mobile-farm-name">
              {farms.find((f) => f.id === farmId)?.name ?? t("common.sidebar.selectFarm")}
            </span>
            <span className={`mobile-farm-arrow ${isDropdownOpen ? "open" : ""}`}>
              ▼
            </span>
          </button>
          {isDropdownOpen && (
            <div className="mobile-farm-menu">
              {farms.map((f) => (
                <button
                  key={f.id}
                  type="button"
                  className={`mobile-farm-option ${farmId === f.id ? "is-active" : ""}${navLocked && farmId !== f.id ? " is-locked" : ""}`}
                  aria-disabled={(navLocked && farmId !== f.id) || undefined}
                  onClick={() => {
                    if (navLocked && farmId !== f.id) {
                      notifyNavLocked();
                      return;
                    }
                    saveStoredFarmId(f.id);
                    dispatch(setFarmId(f.id));
                    setIsDropdownOpen(false);
                  }}
                >
                  {f.name}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      <div className={`sb-farm-list${isAnalysis ? " is-disabled" : ""}`} role="listbox" aria-label={t("common.sidebar.farmsAria")}>
        {farms.length > 0 ? (
          farms.map((f) => (
            <button
              key={f.id}
              type="button"
              role="option"
              aria-selected={farmId === f.id}
              aria-disabled={(navLocked && farmId !== f.id) || undefined}
              className={`sb-farm-item ${farmId === f.id ? "is-active" : ""}${navLocked && farmId !== f.id ? " is-locked" : ""}`}
              disabled={isAnalysis}
              onClick={() => {
                if (isAnalysis) return;
                if (navLocked && farmId !== f.id) {
                  notifyNavLocked();
                  return;
                }
                saveStoredFarmId(f.id);
                dispatch(setFarmId(f.id));
              }}
              title={f.name}
            >
              {f.name}
            </button>
          ))
        ) : (
          <div className="no-farms">{t("common.sidebar.noFarms")}</div>
        )}
        <div ref={sentinelRef} style={{ height: 0.5 }} />
      </div>

      <nav className="sb-tabs" role="tablist" aria-label={t("common.sidebar.tabsAria")}>
        {[
          { key: "Information", label: t("common.sidebar.tabs.information") },
          ...(selectedDrone != null
            ? [{ key: "Mission", label: t("common.sidebar.tabs.mission") }]
            : []),
          { key: "Data", label: t("common.sidebar.tabs.data") },
          { key: "Energy", label: t("common.sidebar.tabs.energy") },
        ].map((item) => {
          // Mission 탭은 항상 열어둔다(미션 중 Stop 버튼 접근 경로). 새로고침 등으로
          // 활성 탭이 Mission이 아닐 때 Mission까지 잠겨 미션을 못 멈추는 일을 방지.
          // 현재 탭과 Mission을 제외한 다른 탭만 미션 중 잠근다.
          const isTabLocked =
            navLocked && item.key !== tab && item.key !== "Mission";
          return (
          <button
            key={item.key}
            className={`sb-tab ${tab === item.key ? "is-active" : ""}${isTabLocked ? " is-locked" : ""}`}
            aria-disabled={isTabLocked || undefined}
            onClick={() => {
              if (isTabLocked) {
                notifyNavLocked();
                return;
              }
              const isMobileViewport =
                typeof window !== "undefined" &&
                window.matchMedia("(max-width: 900px)").matches;
              const next = isMobileViewport && tab === item.key ? null : item.key;
              saveStoredSidebarTab(farmId, next);
              dispatch(setActiveTab(next));
            }}
            role="tab"
            aria-selected={tab === item.key}
            aria-controls={`panel-${item.key.split(" ").join("-")}`}
            type="button"
          >
            {item.label}
          </button>
          );
        })}
      </nav>

      <section className={`sb-panel ${tab ? "is-open" : ""}`} role="tabpanel">
        <div
          className="sb-panel-scroll"
          role="region"
          aria-label={t("common.sidebar.contentAria")}
        >
          {/* 네 탭을 항상 마운트해 두고 display 로만 전환한다.
              탭을 오갈 때 언마운트 → 재마운트로 API 를 다시 부르는 로딩을 없애기 위함.
              에너지 조회는 활성 탭일 때만 진행한다. */}
          <div style={{ display: tab === "Information" ? "contents" : "none" }}>
            <Information />
          </div>
          <div style={{ display: tab === "Mission" ? "contents" : "none" }}>
            <Mission />
          </div>
          <div style={{ display: tab === "Data" ? "contents" : "none" }}>
            <Data />
          </div>
          <div style={{ display: tab === "Energy" ? "contents" : "none" }}>
            <Energy active={tab === "Energy"} />
          </div>
        </div>
      </section>

      {tooltipState.open &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className={`sb-help-floating${tooltipState.isMobile ? " is-mobile" : ""}`}
            role="tooltip"
            style={{
              top: `${tooltipState.top}px`,
              left: `${tooltipState.left}px`,
              width: `${tooltipState.width}px`,
            }}
          >
            {tooltipState.text}
          </div>,
          document.body
        )}
    </aside>
  );
}
